import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { selectMCBySwitch } from 'shared/slice/masterControlBySwitchSelectSlice';
import { selectMCByLocation } from 'shared/slice/masterControlSelectByLocationSlice';
import { selectUnits } from 'shared/slice/unitsSlice';
import { selectEssSwitch } from 'shared/slice/essSwitchSlice';
import { selectTesSwitch } from 'shared/slice/tesSwitchSlice';
import { selectTgsSwitch } from 'shared/slice/tgsSwitchSlice';
import { convertCelsiusToFahrenheit } from 'shared/helpers/helpers';

import styled from 'styled-components';
import {
  justifyContentSpaceBetween,
} from 'shared/styles/commonStyles';

import SelectLocations from './SelectLocations';
import InputTempMessage from 'shared/components/userMessages/inputTempMessage';
import MobileControlLayout from 'features/masterControl/components/shared/MobileControlLayout';

import {
  SwitchCardWrapper,
  SwitchCardTitle,
  SwitchSectionTop,
  SwitchButtonSection,
} from 'features/masterControl/components/shared/styles/switchCardStyles';

import {
  SwitchButtonWrapper,
  SwitchButtonHole,
  SwitchButtonTop,
  MessageBoxWrapper,
} from 'features/masterControl/components/shared/styles/controlButtons';

const SnowSensor = ({
  scope,
  handleOnClick,
  swtName,
  handleClose,
  specificLocation,
  disabled,
}) => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const type = scope === 'switch' ? 'locations' : 'switches';

  const switchStatus = useSelector(
    scope === 'switch' ? selectMCBySwitch : selectMCByLocation
  );
  const { selectedOne } = switchStatus.snowSensor;
  const { flatEssSwitch } = useSelector(selectEssSwitch);
  const { flatTesSwitch } = useSelector(selectTesSwitch);
  const { flatTgsSwitch } = useSelector(selectTgsSwitch);

  const unitsStatus = useSelector(selectUnits);
  const { isF } = unitsStatus;

  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [messages, setMessages] = useState([]);
  const switchData =
    swtName === 'ess'
      ? flatEssSwitch
      : swtName === 'tes'
      ? flatTesSwitch
      : flatTgsSwitch;
  const selectedMachine = switchStatus.snowSensor.selectedMachines?.[0];
  const selectedLocation = selectedMachine?.[0];
  const selectedMachineId =
    selectedMachine?.length > 2 ? selectedMachine[2] : selectedMachine?.[1];
  const selectedSpecificLocation =
    selectedMachine?.length > 2 ? selectedMachine[1] : null;
  const defaultTemp =
    (selectedSpecificLocation
      ? switchData?.[selectedLocation]?.[selectedSpecificLocation]?.[
          selectedMachineId
        ]?.snowSensor?.defaultTemp
      : switchData?.[selectedLocation]?.[selectedMachineId]?.snowSensor
          ?.defaultTemp) || 350;
  const displayDefaultTemp = isF
    ? convertCelsiusToFahrenheit(defaultTemp)
    : defaultTemp;

  const handleApply = () => {
    if (!selectedOne) {
      handleOnClick(
        'snowSensor',
        'selectA',
        scope,
        '_',
        '_',
        type,
        specificLocation
      );
      setOpenMessageBox(true);
      setMessages(['select locations', 'please select location to continue']);
    } else {
      handleOnClick('snowSensor', 'on', scope);

      if (isMobile && scope !== 'switch') {
        handleClose();
      }
    }
  };

  if (isMobile) {
    return (
      <MobileControlLayout
        scope={scope}
        swtName={swtName}
        specificLocation={specificLocation}
        controlName='snowSensor'
        logoSrc='images/logo-snowSensor.svg'
        label={
          <>
            snow sensor<br />program
          </>
        }
        labelSmall='snow sensor program'
        onApply={handleApply}
        subtitle='snow sensor program'
        messages={messages}
        openMessageBox={openMessageBox}
        onCloseMessageBox={() => setOpenMessageBox(false)}
      />
    );
  }

  return (
    <SwitchCardWrapper $watermark='images/logo-snowSensor.svg'>
      <SwitchSectionTop>
        <SwitchCardTitle>snow sensor program</SwitchCardTitle>

        <SelectLocations
          size={1}
          name='snowSensor'
          scope={scope}
          swtName={swtName}
          specificLocation={specificLocation}
        />
      </SwitchSectionTop>

      <SectionController>
        <SectionDisplay>
          <DisplayTop>
            <DisplayTitle>
              default trigger <br />temperature
            </DisplayTitle>
            <Divider />
            <DefaultTemp>
              {displayDefaultTemp} {isF ? '\u00B0F' : '\u00B0C'}
            </DefaultTemp>
          </DisplayTop>
          <InfoDefaultTemp>
            snow trigger temperature is managed in settings
          </InfoDefaultTemp>
        </SectionDisplay>

        <SwitchButtonSection>
          <SwitchButtonWrapper onClick={handleApply} disabled={disabled}>
            <SwitchButtonHole disabled={disabled}>
              <SwitchButtonTop disabled={disabled}>apply</SwitchButtonTop>
            </SwitchButtonHole>
          </SwitchButtonWrapper>
        </SwitchButtonSection>

        {openMessageBox && (
          <MessageBoxWrapper>
            <InputTempMessage
              onClose={() => setOpenMessageBox(false)}
              title='master control'
              messages={messages}
            />
          </MessageBoxWrapper>
        )}
      </SectionController>
    </SwitchCardWrapper>
  );
};

export default SnowSensor;




const SectionController = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  position: relative;
  z-index: 1;
`;

const SectionDisplay = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DisplayTop = styled.div`
  width: 100%;
  height: 50.6px;
  border-radius: 14px;
  border: 0.8px solid var(--border-input);
  background: var(--bg-input);
  box-shadow: inset 0px 2px 8px rgba(0, 0, 0, 0.6);
  ${justifyContentSpaceBetween}
  padding: 0 12.8px;
`;

const DisplayTitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 12.5px;
  color: var(--switch-text-muted);
`;

const Divider = styled.div`
  height: 24px;
  width: 1px;
  background: var(--border-input);
`;

const DefaultTemp = styled.span`
  font-family: 'Consolas', monospace;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.7px;
  color: var(--switch-text-primary);
`;

const InfoDefaultTemp = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 9px;
  line-height: 13.5px;
  color: var(--switch-text-faint);
  text-align: center;
  width: 100%;
  display: block;
`;
